/**
 * Adding a parameter to a destination that may already have one.
 *
 * Several auth hops need to attach something to a place the visitor is about to be sent — the
 * post-reset destination, the ODTP token surviving a magic-link login. Every one of them used to do
 * it by writing `` `${destination}?${param}` ``, which is correct only while the destination has no
 * query of its own.
 *
 * It does have one, routinely: the Speedy Flow's resume state rides on `?sf=`. A second `?` is not
 * a separator — the browser splits a query on `&` alone — so the whole thing parses as **one**
 * parameter whose value happens to end in `?odtp=TZ-2024`. The consequences are silent: `sf` fails
 * to `JSON.parse` and the visitor's product choice is dropped, and `odtp` is never seen as a
 * parameter at all, so a day ticket somebody paid a partner for is never redeemed.
 *
 * The same mistake, in the same shape, cost the email verification link once already — see
 * `buildVerificationURL` in the API's `commons/utils`.
 */

/**
 * Take a destination apart.
 *
 * Deliberately string-based rather than `new URL(...)`. The destinations here are not all URLs the
 * parser accepts against a base: they may be in-app paths (`/h/kea?sf=…`), absolute URLs, or the
 * native-app deep links (`zoolife://…`) the third-party login hands back. Splitting the string
 * treats all three alike, and none of them needs the parser's normalisation.
 */
const split = (destination: string) => {
  // The fragment comes off first: it is terminated by nothing, so anything appended after it
  // becomes part of the fragment rather than the query.
  const hashIndex = destination.indexOf('#');
  const hash = hashIndex === -1 ? '' : destination.slice(hashIndex);
  const withoutHash = hashIndex === -1 ? destination : destination.slice(0, hashIndex);

  const queryIndex = withoutHash.indexOf('?');
  return {
    base: queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex),
    search: queryIndex === -1 ? '' : withoutHash.slice(queryIndex + 1),
    hash,
  };
};

/** Set `key` to `value` on `destination`, preserving whatever query and fragment it already has. */
export const withQueryParam = (destination: string, key: string, value: string): string => {
  const { base, search, hash } = split(destination);

  const params = new URLSearchParams(search);
  params.set(key, value);

  return `${base}?${params.toString()}${hash}`;
};

/**
 * Read `key` back off a destination, whatever else it carries.
 *
 * The other half of the round trip, and it has to split the string the same way. Reading with
 * `URLSearchParams` alone means the *last* parameter of a destination with a fragment comes back
 * with the fragment stuck to it — `zlpopup=1#receipt` reads as `1#receipt`, and a marker written by
 * `withQueryParam` fails to match the value it was written with.
 */
export const readQueryParam = (destination: string, key: string): string | null =>
  new URLSearchParams(split(destination).search).get(key);
